import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight } from '@untitled-ui/icons-react';
import type { TFunctionNonStrict } from 'i18next';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import { Button } from '../../../../../components/buttons/button';
import { setCustomMetaTitle } from '../../../../../components/meta-title';
import { MobileBlocker } from '../../../../../components/mobile-blocker';
import { SectionCheckbox } from '../../../../../components/product-template-editor/section-checkbox';
import { ToastVariant } from '../../../../../components/toast';
import { ToastBanner, ToastBannerVariant } from '../../../../../components/toast-banner';
import {
  BulkGroupStatus,
  INSUFFICIENT_CREDITS_FOR_BULK_GENERATION,
  UNREACHABLE_PRODUCT_IMAGE,
} from '../../../../../entity/api/bulk-group';
import { AppError } from '../../../../../error';
import { trackError } from '../../../../../error/tracking';
import { captureException } from '../../../../../logger/capture-exception';
import { getQueryClient } from '../../../../../sdk/request';
import {
  fetchBulkGroupsListImperative,
  useBulkGroupById,
  useUpdateBulkGroup,
} from '../../../../../services/storage';
import { useOrganizationBilling } from '../../../../../services/storage/organization-billing-queries';
import { useOrganizationById } from '../../../../../services/storage/organizations-queries';
import { tGlobal, tNamespace } from '../../../../../services/translation';
import { useToast } from '../../../../../shared/contexts/toast-context';
import { getOrgCredit } from '../../../../../shared/credits';
import { Feature, isFeatureAvailable } from '../../../../../shared/feature';
import { ProductDropdown } from './-components/product-dropdown';

const t = tNamespace('app.funnel.bulk-import.$id.index');
const tErrors = tNamespace('errors');

function navigateTo(path: string, navigate: ReturnType<typeof useNavigate>) {
  navigate({ to: path }).catch(function (err) {
    captureException(
      err,
      {
        file: 'routes/app/funnel/bulk-import/$id/index.tsx',
        method: 'useEffect',
        infoMessage: `unable to navigate to ${path}`,
      },
      trackError
    );
  });
}

export const Route = createFileRoute('/app/funnel/bulk-import/$id/')({
  beforeLoad: function () {
    const queryClient = getQueryClient();
    // NOTE: we may want to query only the bulk group by id at this stage in the future
    return Promise.allSettled([fetchBulkGroupsListImperative(queryClient)]).catch(function (err) {
      captureException(
        err,
        {
          file: 'routes/app/funnel/bulk-import/$id/index.tsx',
          method: 'loader',
          infoMessage: 'unable to fetch bulk import list from api',
        },
        trackError
      );

      throw err;
    });
  },
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { id: bulkGroupId }: { id: string } = Route.useParams();
  const [isToggled, setIsToggled] = useState<string | null>('0');
  const addToast = useToast();
  const [loadingSaveDraft, setLoadingSaveDraft] = useState(false);
  const [draftTimeout, setDraftTimeout] = useState<number | null>(null);
  const [loadingGenerateProducts, setLoadingGenerateProducts] = useState(false);
  const [areImagesRequired, setAreImagesRequired] = useState(false);

  // TanStack Query hooks
  const { data: bulkGroup } = useBulkGroupById(bulkGroupId);
  const updateBulkGroupMutation = useUpdateBulkGroup();

  // TanStack Query for organization and billing
  const { data: organization } = useOrganizationById(bulkGroup?.organizationId);
  const { data: orgBilling } = useOrganizationBilling(organization?._id);

  const creditData = getOrgCredit(orgBilling);

  const numberOfProducts = bulkGroup?.numberOfProducts ?? 0;
  // TODO: this shouldn't be hard coded here
  const creditsRequired = numberOfProducts; // each product is to be treated as 1 credit

  const insufficientCredits = creditsRequired > creditData.available;

  function saveAsDraft() {
    setLoadingSaveDraft(true);
    const timeout = window.setTimeout(function () {
      setLoadingSaveDraft(false);
      addToast({
        id: Date.now(),
        title: t('savedAsDraft'),
        variant: ToastVariant.Success,
      });
      navigateTo('/app/products/bulk-import', navigate);
    }, 1000);
    setDraftTimeout(timeout);
  }

  function confirmAndGenerateProducts() {
    if (organization == null) {
      addToast({
        id: Date.now(),
        title: t('errorGeneratingProducts'),
        variant: ToastVariant.Error,
      });
      return;
    }

    if (creditData.available < creditsRequired) {
      addToast({
        id: Date.now(),
        title: t('notEnoughCredits'),
        description: t('notEnoughCreditsDescription', {
          credits: creditData.available,
          neededCredits: creditsRequired,
        }),
        variant: ToastVariant.Error,
        className: 'w-full max-w-[720px]',
        descriptionClassName: 'max-w-3xl',
        cta: {
          text: t('upgradePlan'),
          onClick: function () {
            navigateTo(`/app/settings/organization/${bulkGroup?.organizationId}/plan`, navigate);
          },
        },
      });
      return;
    }

    setLoadingGenerateProducts(true);
    updateBulkGroupMutation.mutate(
      {
        id: bulkGroupId,
        data: {
          areImagesRequired,
          status: BulkGroupStatus.PROCESSING,
        },
      },
      {
        onSuccess: function () {
          navigateTo('/app/funnel/bulk-import/$id/loading', navigate);
        },
        onError: function (err) {
          let errorTitle = t('errorGeneratingProducts');
          if (err instanceof AppError && err.message === INSUFFICIENT_CREDITS_FOR_BULK_GENERATION) {
            errorTitle = t('notEnoughCredits');
          } else if (err instanceof AppError && err.message === UNREACHABLE_PRODUCT_IMAGE) {
            errorTitle = tErrors(err.originalMessage);
          }
          addToast({
            id: Date.now(),
            title: errorTitle,
            variant: ToastVariant.Error,
          });
          captureException(
            err,
            {
              file: 'routes/app/funnel/bulk-import/$id/index.tsx',
              method: 'confirmAndGenerateProducts',
              infoMessage: 'unable to update bulk group status to processing',
              areImagesRequired,
            },
            trackError
          );
        },
        onSettled: function () {
          setLoadingGenerateProducts(false);
        },
      }
    );
  }

  useEffect(
    function () {
      return function () {
        if (draftTimeout != null) {
          window.clearTimeout(draftTimeout);
        }
      };
    },
    [draftTimeout]
  );

  useEffect(
    function () {
      if (!isFeatureAvailable(Feature.PRODUCT)) {
        navigateTo('/app/blogs', navigate);
      }
    },
    [navigate]
  );

  // NOTE: we are seeing the screen for a small amount of time before redirect, need to improve this
  useEffect(
    function () {
      // Redirect to products page if no bulk group exists
      if (bulkGroup == null) {
        navigateTo('/app/products', navigate);
        return;
      }
      setCustomMetaTitle(`${tGlobal('funnel')} ${bulkGroup.title}`, false);

      // Handle different bulk group statuses
      switch (bulkGroup.status) {
        case BulkGroupStatus.PROCESSING:
          navigateTo(`/app/funnel/bulk-import/$id/loading`, navigate);
          break;
        case BulkGroupStatus.COMPLETED:
          navigateTo('/app/products', navigate);
          break;
      }
    },
    [bulkGroup, navigate, bulkGroupId]
  );

  if (bulkGroup == null || organization == null) {
    return null;
  }

  return (
    <>
      <MobileBlocker />
      <div className="relative h-full w-full animate-fade-in bg-white py-20">
        <div className="h-full overflow-auto pb-10" style={{ scrollbarGutter: 'stable' }}>
          <div className="mx-auto flex w-full max-w-content flex-col items-center gap-8">
            <h2 className="font-display-2">{t('titlePage')}</h2>
            <div className="w-full text-center">
              <Trans
                t={t as unknown as TFunctionNonStrict}
                i18nKey="detectedProducts"
                count={bulkGroup.numberOfProducts ?? 0}
              />
            </div>
            <div className="flex w-full flex-col gap-4">
              {bulkGroup.rawProducts?.map(function (product) {
                return (
                  <ProductDropdown
                    key={product.row}
                    product={product}
                    isToggled={isToggled === product.row?.toString()}
                    onToggle={function (id: string) {
                      setIsToggled(id === isToggled ? null : id);
                    }}
                  />
                );
              })}
            </div>
            <div className="w-full">
              <SectionCheckbox
                checked={areImagesRequired}
                onCheckedChange={function (value) {
                  setAreImagesRequired(value);
                }}
                title={t('requireImages')}
                subTitle={t('requireImagesDescription')}
              />{' '}
            </div>
          </div>
        </div>
        <div className="fixed bottom-0 left-0 w-full border-t border-t-gray-200 bg-white p-4">
          <div className="mx-auto flex w-[800px] items-center justify-between">
            <Button
              variant="outline"
              onClick={function () {
                navigate({ to: '/app/products/bulk-import' }).catch(function (err) {
                  captureException(
                    err,
                    {
                      file: 'routes/app/funnel/bulk-import/$id/index.tsx',
                      method: 'useEffect',
                      infoMessage: 'unable to navigate to /app/products',
                    },
                    trackError
                  );
                });
              }}
            >
              {tGlobal('cancel')}
            </Button>
            <div className="flex gap-4">
              <Button variant="outline" onClick={saveAsDraft} loading={loadingSaveDraft}>
                {t('saveAsDraft')}
              </Button>
              <Button
                variant="accent"
                rightIcon={<ArrowRight width={16} height={16} />}
                onClick={confirmAndGenerateProducts}
                loading={loadingGenerateProducts}
                disabled={insufficientCredits}
              >
                {t('confirmAndGenerateProducts')}
              </Button>
            </div>
          </div>
        </div>

        {/* Insufficient redits notice */}
        {insufficientCredits ? (
          <div className="absolute bottom-[106px] left-1/2 w-[620px] -translate-x-1/2">
            <ToastBanner
              title={t('notEnoughCredits')}
              description={t('notEnoughCreditsDescription', {
                credits: creditData.available,
                neededCredits: creditsRequired,
              })}
              variant={ToastBannerVariant.Error}
              size="sm"
            />
          </div>
        ) : null}
      </div>
    </>
  );
}
